import { useContext, useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useAuth } from 'hooks/useAuth';

import { MENU_EDITOR } from 'constants/chownow';
import { CUBOH_COMPANY_PREFIX } from 'constants/path';
import { HeadquarterContext } from 'context/HeadquarterContext';

import { getValidCubohCompanyID } from 'helpers/auth.cuboh';
import { useGetMisRestaurant } from 'api/entitlements';

import { useCubohActionBar } from './useCubohActionBar';

export default function useProvideScopes() {
  const flags = useFlags();

  // simple hook for combining relevant data to handle scopes
  const {
    hasChowNowUser,
    hasCubohUser,
    isUserCubohOnly,
    isUserChowNowOnly,
    isUserCubohAndChowNow,
    isInternalUser,
    user,
    companies,
    isRegionalAdminUser,
    cubohUser,
  } = useAuth();
  const { location, cubohCompany } = useContext(HeadquarterContext);
  const { setActionBarSingleSelectedOption, singleSelectedCompanyID } = useCubohActionBar();
  const { restaurantId: restaurantIdFromParams } = useParams();

  const misQuery = useGetMisRestaurant(restaurantIdFromParams);
  const misScopes = misQuery.data?.metadata?.scopes ?? [];

  const validCubohLocationIDtoUse = useMemo(
    () =>
      getValidCubohCompanyID({
        isUserCubohOnly,
        singleSelectedCompanyID,
        location,
        companies,
      }),
    [isUserCubohOnly, singleSelectedCompanyID, location, companies],
  );

  const scopeDetails = useMemo(
    () => ({
      hasChowNowUser,
      hasCubohUser,
      isUserCubohOnly,
      isUserChowNowOnly,
      isUserCubohAndChowNow,
      isInternalUser,
      isRegionalAdminUser,
      userScopes: user?.metadata?.scopes || [],
      locationScopes: location?.metadata?.scopes || [],
      hasValidCubohLocationID: !!validCubohLocationIDtoUse,
      cubohUserMeta: cubohUser?.userDetails?.userMeta || {},
      cubohIsParentCompanyUser: cubohUser?.isParentCompanyUser,
      isLegacyMenuEditor: location?.menu_editor === MENU_EDITOR.legacy,
      companies,
      location,
      cubohCompany,
      flags,
      misScopes,
    }),
    [
      hasChowNowUser,
      hasCubohUser,
      isUserCubohOnly,
      isUserChowNowOnly,
      isUserCubohAndChowNow,
      user,
      cubohUser,
      location,
      cubohCompany,
      validCubohLocationIDtoUse,
      companies,
      flags,
      misScopes,
    ],
  );

  // used in the url for navigating between pages
  // fall back to the URL param so nav links stay valid when location is temporarily undefined
  const locationIDToUseForNavigation = isUserCubohOnly
    ? `${CUBOH_COMPANY_PREFIX}${singleSelectedCompanyID}`
    : location?.id ?? restaurantIdFromParams;

  /**
   * If the validCubohLocationID (CN) is different from the single selected company id (Cuboh)
   * force them to be in sync if we can (CN select as source of truth).
   *
   * This assumes that CN/Cuboh mixed locations will only have 1 merchant.
   */
  useEffect(() => {
    if (isUserCubohOnly) return;

    if (
      validCubohLocationIDtoUse &&
      Number(validCubohLocationIDtoUse) !== Number(singleSelectedCompanyID)
    ) {
      setActionBarSingleSelectedOption(
        JSON.stringify({
          companyID: Number(validCubohLocationIDtoUse),
        }),
      );
    }
  }, [validCubohLocationIDtoUse, singleSelectedCompanyID]);

  return {
    scopeDetails,
    validCubohLocationIDtoUse,
    locationIDToUseForNavigation,
  };
}
